import * as React from "react";
import { useEffect, useState } from "react";
import { addMonths, addYears } from "../../../../../react-core/utils/date";
import { ServiceProposition } from "../../../../../Wrap/Constants/Enums/ServiceProposition";
import { UserClaim } from "../../../../../Wrap/Constants/Enums/UserClaim";
import { Adviser } from "../../../../../Wrap/Models/Domains/Adviser/Adviser";
import { Alert } from "../../../../Shared/Components/Alert";
import { AlertType } from "../../../../Shared/Entities/Constants/AlertConstants";
import { ExcelExportAction } from "../../../../Shared/ExcelExport/Actions/ExcelExportAction";
import { ExcelExportButton } from "../../../../Shared/ExcelExport/ExcelExportButton";
import AdviserSiteTagManager, {
    eventCategoryForGTM,
} from "../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { LoadingSkeletonType } from "../../../../Shared/LoadingSkeleton/Contents";
import { LoadingSkeletonWrapper } from "../../../../Shared/LoadingSkeleton/LoadingSkeletonWrapper";
import { checkPermission } from "../../../../Shared/Permission/PermissionHelper";
import { getQueryParameters } from "../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { BusinessManagementAction } from "../Action/BusinessManagementAction";
import { BMContent } from "../Resource/Content";
import { ChargesColumnChart } from "./Charges/ChargesColumnChart";
import { ExpandableTableCharges } from "./Charges/ExpandableTableCharges";
import { AdviserDropdown } from "./Search/AdviserDropdown";
import { UserStore } from "../../../Stroes/Variable";
import { JsonDateParser } from "../../../../../react-core/Utils/JsonDateParser";
import { CommonHelper } from "../../../../../react-core/Helpers/CommonHelper";
import { DateOptions } from "../../../../Shared/Components/Resource/Contents";

interface IChargesProps {
    selectedProposition: ServiceProposition[];
}

export const Charges = (props: IChargesProps) => {
    enum Views {
        Table,
        Chart,
    }
    const [view, setView] = useState<Views>(Views.Chart);
    const [data, setData] = useState(null);
    const [isLoading, setisLoading] = useState(false);
    const [fromDate, setFromDate] = useState<Date>(addMonths(new Date(), -3));
    const [toDate, setToDate] = useState<Date>(new Date());
    const [companyHierarchyId, setCompanyHierarchyId] = useState<string>(getQueryParameters()["firm"]);
    const [adviserHierarchyId, setAdviserHierarchyId] = useState<string>(null);

    useEffect(() => {
        search(null, addMonths(new Date(), -3), new Date(), null);
    }, [props.selectedProposition]);

    useEffect(() => {
        addEvent({
            environment: {
                appType: "web",
                setting: "prod",
                timestamps: JsonDateParser.toJsonDateNoOffset(new Date()).toString(),
            },
            page: {
                pageUrl: window.location.pathname.includes("advisercharges")
                    ? window.location.pathname
                    : window.location.pathname + "/advisercharges",
                pageTitle: `${document.title} - business management - adviser charges`,
            },
            user: {
                loginId: UserStore.getUserInfo()?.ClientId.toString(),
                internal: "yes",
            },
            event: "pageLoaded",
        });
    }, []);
    const { addEvent } = new AdviserSiteTagManager();
    const search = (advisor: Adviser, from, to, dateSelected) => {
        if (dateSelected) {
            addEvent({
                event: "adviserchargestab_interaction",
                eventParameters: {
                    eventAction: "interactions",
                    eventCategory: eventCategoryForGTM.adviserChargesTab,
                    eventLabel: `submit - ${CommonHelper.getOptionsTxt(
                        dateSelected,
                        DateOptions
                    ).toLowerCase()} - adviser charges`,
                },
            });
        }
        setisLoading(true);
        BusinessManagementAction.getAdviserCharges(
            getQueryParameters()["firm"],
            advisor?.HierarchyId,
            from,
            to,
            props.selectedProposition,
            (d) => {
                setisLoading(false);
                setData(d);
                setFromDate(from);
                setToDate(to);
                setAdviserHierarchyId(advisor?.HierarchyId);
            }
        );
    };

    return (
        <div
            id={"panel-Adviser charges"}
            role="tabpanel"
            tabIndex={0}
            aria-labelledby={"tab-Adviser charges"}
            className="mb-40"
        >
            <h2 className="mb-16">Adviser charges</h2>
            <AdviserDropdown
                onSearch={(advisor, from, to, dateSelected) => search(advisor, from, to, dateSelected.toLowerCase())}
                isFromTo
            />
            <LoadingSkeletonWrapper isLoading={isLoading} skeletonType={LoadingSkeletonType.Table}>
                <div className="row narrow-gutter row-right mb-24">
                    <div className="col-auto mb-20">
                        <button
                            onClick={() => {
                                addEvent({
                                    event: "adviserchargestab_interaction",
                                    eventParameters: {
                                        eventAction: "interactions",
                                        eventCategory: eventCategoryForGTM.adviserChargesTab,
                                        eventLabel: "adviser charges - chart",
                                    },
                                });
                                setView(Views.Chart);
                            }}
                            aria-pressed={view == Views.Chart}
                            className="column-chart-view-button"
                            title="Column chart view"
                        >
                            <span className="sr-only">Set pie chart view</span>
                            <span className="icon" aria-hidden={true}></span>
                        </button>
                    </div>
                    <div className="col-auto">
                        <button
                            onClick={() => {
                                addEvent({
                                    event: "adviserchargestab_interaction",
                                    eventParameters: {
                                        eventAction: "interactions",
                                        eventCategory: eventCategoryForGTM.adviserChargesTab,
                                        eventLabel: "adviser charges - table",
                                    },
                                });
                                setView(Views.Table);
                            }}
                            aria-pressed={view == Views.Table}
                            className="table-view-button"
                            title="Data table view"
                        >
                            <span className="sr-only">Set table view</span>
                            <span className="icon" aria-hidden={true}></span>
                        </button>
                    </div>
                    <div className="col-auto">
                        <ExcelExportButton
                            onRequestReport={() => {
                                addEvent({
                                    event: "adviserchargestab_interaction",
                                    eventParameters: {
                                        eventAction: "interactions",
                                        eventCategory: eventCategoryForGTM.adviserChargesTab,
                                        eventLabel: "export to excel",
                                    },
                                });
                                return ExcelExportAction.generateAdviserChargeReport(
                                    companyHierarchyId,
                                    adviserHierarchyId,
                                    fromDate,
                                    toDate,
                                    props.selectedProposition
                                );
                            }}
                            selectedAccount={null}
                        ></ExcelExportButton>
                    </div>
                </div>
                <Alert alertType={AlertType.Warning} message={BMContent.FBRCWarning} />
                <div className="panel-subtle mb-40">
                    {view == Views.Chart ? <ChargesColumnChart data={data} /> : <ExpandableTableCharges data={data} />}
                </div>
            </LoadingSkeletonWrapper>
            {checkPermission(UserClaim.AdviserPortalLinkAdviserFirmTermsCanView) && (
                <a
                    className="hyperlink-next"
                    href="/admin/termsmanagement/viewadviserfirmtermshistory.aspx"
                    onClick={() => {
                        addEvent({
                            event: "adviserchargestab_interaction",
                            eventParameters: {
                                eventAction: "interactions",
                                eventCategory: eventCategoryForGTM.adviserChargesTab,
                                eventLabel: "adviser firm terms",
                            },
                        });
                    }}
                >
                    Click to see your adviser firm terms
                </a>
            )}
        </div>
    );
};
